import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { mockFetchStripePlans } from '../dev/mockBilling'
import { isDevMock } from '../dev/isDevMock'
import { useSubscription } from '../hooks/useSubscription'
import { useTeams } from '../hooks/useProjectTeam'
import { apiFetch } from '../lib/apiFetch'
import { useGetStripePlansQuery } from '../services/projectDataApi'
import {
  normalizeBackendPlan,
  groupPlansByTier,
  planDisplayName,
  tierFromPlanKey,
  PLAN_CONTENT,
  PlanTier,
  PlanTierGroup,
  StripePlan,
} from '../lib/stripe-config'
import { trackSegmentEvent } from '../lib/segment'
import { trackEvent } from '../lib/analytics'
import './PlanSelection.css'

type BillingInterval = 'month' | 'year'
export type PlanSurface = 'settings' | 'gate'

// Cheapest → most expensive, for classifying a plan switch as an upgrade or a downgrade.
const TIER_RANK: Record<PlanTier, number> = {
  free: 0, individual: 1, team: 2, growth: 3, enterprise: 4, 'private-cloud': 5, 'on-prem': 6,
}

// A plan change is an upgrade when the target tier ranks at or above the current one, else a
// downgrade. Current tier is read off the plan key prefix (e.g. "individual-v1" → "individual").
function planChangeDirection(currentPlanKey: string | undefined, targetTier: PlanTier): 'upgrade' | 'downgrade' {
  const currentTier = (tierFromPlanKey(currentPlanKey) || 'free') as PlanTier
  return (TIER_RANK[targetTier] ?? 0) >= (TIER_RANK[currentTier] ?? 0) ? 'upgrade' : 'downgrade'
}

/**
 * PlanSelection — plan tiers with a monthly/yearly billing toggle. Plans are
 * fetched from the Replay backend via /api/stripe-plans. Ported from the
 * dashboard's PlanSelection.
 *
 * `surface` records where the picker was shown: the Subscription tab someone chose to open, or the
 * paywall they ran into. Both reach the same checkout, but only one of them is a decision.
 */
export function PlanSelection({ surface = 'settings' }: { surface?: PlanSurface } = {}) {
  const { subscription, isLoading: isSubLoading, workspaceId } = useSubscription()
  // Someone who belongs to a team they don't own is looking at plans for a reason that isn't obvious:
  // subscribing here funds THEIR team, not the one they were invited to. Say so, or the page reads as
  // if their teammate's balance depends on them buying something. Which team that is, and what it has,
  // is the account selector's business — this only draws the distinction.
  const { teams } = useTeams()
  const inOtherTeam = teams.some((team) => !team.is_owner)
  const [interval, setBillingInterval] = useState<BillingInterval>('month')

  // The plan catalog is static per session and both surfaces (settings tab, credit gate) can be
  // mounted together, so it comes from the shared RTK Query endpoint — the same treatment
  // useSubscription got, whose per-mount fetch had been issuing one request per surface. RTK Query
  // dedupes concurrent subscribers and serves remounts from the cache instead of refetching. In
  // UI-only dev-mock mode there is no backend, so the mocked catalog stands in.
  const devMock = isDevMock()
  const plansQuery = useGetStripePlansQuery(undefined, { skip: devMock })
  const planGroups: PlanTierGroup[] | null = devMock
    ? groupPlansByTier(mockFetchStripePlans().plans.map(normalizeBackendPlan))
    : (plansQuery.data ?? null)
  const plansLoading = devMock ? false : plansQuery.isLoading
  const plansError = devMock || !plansQuery.error ? null : 'Failed to load plans'
  const refetchPlans = devMock ? () => {} : () => { void plansQuery.refetch() }

  const isLoading = isSubLoading || plansLoading

  // Reaching the plans is the step the upgrade funnel was missing entirely: without it there's no way
  // to tell "nobody looks" from "everybody looks and nobody buys". Fires once the plans have loaded,
  // so it counts people who saw prices rather than people who saw a spinner.
  const viewed = useRef(false)
  useEffect(() => {
    if (viewed.current || isLoading || plansError) return
    viewed.current = true
    trackEvent('upgrade-viewed', {
      surface,
      current_plan: subscription?.plan.key ?? 'free',
      has_subscription: !!subscription && subscription.status !== 'canceled',
    })
  }, [isLoading, plansError, subscription, surface])

  if (isLoading) {
    return <div className="plan-selection__status">Loading plans…</div>
  }

  if (plansError) {
    return (
      <div className="plan-selection__error">
        <p>{plansError}</p>
        <button
          type="button"
          className="plan-card__btn"
          style={{ width: 'auto', alignSelf: 'center' }}
          onClick={refetchPlans}
          data-analytics-event="plan-retry-button"
        >
          Retry
        </button>
      </div>
    )
  }

  const groups = planGroups ?? []
  const hasActiveSubscription = !!subscription && subscription.status !== 'canceled'

  return (
    <div className="plan-selection">
      <div className="plan-selection__header">
        <div className="plan-selection__intro">
          <h2 className="plan-selection__title">Choose a Plan</h2>
          {hasActiveSubscription ? (
            <>
              <p className="plan-selection__subtitle">
                You are currently on the{' '}
                <strong>
                  {planDisplayName(subscription!.plan.name, tierFromPlanKey(subscription!.plan.key))}
                </strong>{' '}
                plan.
              </p>
              {subscription!.cancelAtPeriodEnd && subscription!.currentPeriodEnd && (
                <p className="plan-selection__scheduled">
                  Update scheduled — current plan active until{' '}
                  {new Date(subscription!.currentPeriodEnd * 1000).toLocaleDateString()}
                </p>
              )}
            </>
          ) : (
            <p className="plan-selection__subtitle">Select a plan to get started with Replay.</p>
          )}
          {inOtherTeam && (
            <p className="plan-selection__subtitle" data-testid="plan-guest-team-note">
              A plan here covers projects in your own team. Teams you’ve joined are already paid for by
              their owner’s plan.
            </p>
          )}
        </div>

        <div className="plan-selection__toggle">
          <button
            type="button"
            onClick={() => setBillingInterval('month')}
            className={`plan-selection__toggle-btn${
              interval === 'month' ? ' plan-selection__toggle-btn--active' : ''
            }`}
            data-analytics-event="plan-interval-month-toggle"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('year')}
            className={`plan-selection__toggle-btn${
              interval === 'year' ? ' plan-selection__toggle-btn--active' : ''
            }`}
            data-analytics-event="plan-interval-year-toggle"
          >
            Yearly
            <span className="plan-selection__save-badge">Save 14%</span>
          </button>
        </div>
      </div>

      <div className="plan-selection__grid">
        {groups.map((tierGroup) => (
          <PlanCard
            key={tierGroup.tier}
            tierGroup={tierGroup}
            interval={interval}
            currentPlanKey={
              subscription?.status !== 'canceled' ? subscription?.plan.key : undefined
            }
            hasActiveSubscription={hasActiveSubscription}
            cancelAtPeriodEnd={subscription?.cancelAtPeriodEnd ?? false}
            workspaceId={workspaceId}
            surface={surface}
          />
        ))}
      </div>
    </div>
  )
}

function PlanCard({
  tierGroup,
  interval,
  currentPlanKey,
  hasActiveSubscription,
  cancelAtPeriodEnd,
  workspaceId,
  surface,
}: {
  tierGroup: PlanTierGroup
  interval: BillingInterval
  currentPlanKey: string | undefined
  hasActiveSubscription: boolean
  cancelAtPeriodEnd: boolean
  workspaceId: string | null
  surface: PlanSurface
}) {
  const plan: StripePlan = interval === 'month' ? tierGroup.monthly : tierGroup.yearly
  const isCurrentPlan = currentPlanKey === plan.key
  const isExistingSubscriberChange =
    hasActiveSubscription && !isCurrentPlan && !tierGroup.contactSales
  // Free → paid upgrades use Checkout; paid → paid switches use the portal
  // (avoids Stripe's per-seat "Quantity: 0" issue).
  const currentPlanIsFree = currentPlanKey?.startsWith('free') ?? false
  const usePortalForChange = isExistingSubscriberChange && !currentPlanIsFree
  const [isPending, setIsPending] = useState(false)

  const handleSubscribe = useCallback(async () => {
    if (isPending) return
    setIsPending(true)
    try {
      const res = await apiFetch('/api/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planKey: plan.key, workspaceId: workspaceId ?? undefined }),
      })
      if (!res.ok) {
        console.error('[PlanCard] checkout error:', res.status)
        setIsPending(false)
        return
      }
      const data = (await res.json()) as { url?: string }
      if (data.url) {
        trackEvent('checkout-started', { plan: plan.key, interval: plan.interval, surface })
        window.location.href = data.url
      } else {
        setIsPending(false)
      }
    } catch (err) {
      console.error('[PlanCard] checkout error:', err)
      setIsPending(false)
    }
  }, [isPending, plan.key, plan.interval, surface, workspaceId])

  const handleOpenPortal = useCallback(async () => {
    if (isPending || !workspaceId) return
    setIsPending(true)
    try {
      const res = await apiFetch('/api/stripe-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId }),
      })
      if (!res.ok) {
        console.error('[PlanCard] portal error:', res.status)
        setIsPending(false)
        return
      }
      const data = (await res.json()) as { url?: string }
      if (data.url) {
        trackEvent('billing-portal-opened', { source: 'plan-card', plan: plan.key, surface })
        window.location.href = data.url
      } else {
        setIsPending(false)
      }
    } catch (err) {
      console.error('[PlanCard] portal error:', err)
      setIsPending(false)
    }
  }, [isPending, plan.key, surface, workspaceId])

  const isHighlighted = tierGroup.highlighted
  const content = PLAN_CONTENT[tierGroup.tier]
  const tagline = content?.tagline ?? tierGroup.displayName
  const description = content?.description ?? null
  const note = content?.note ?? null
  const { priceNumber, pricePeriod } = formatPrice(plan)

  return (
    <div className={`plan-card${isHighlighted ? ' plan-card--highlighted' : ''}`}>
      {isHighlighted && <div className="plan-card__badge">MOST POPULAR</div>}

      <div className="plan-card__head">
        <div className="plan-card__tier">{tierGroup.displayName}</div>
        <div className="plan-card__price">
          <div className="plan-card__price-number">{priceNumber}</div>
          {pricePeriod && <div className="plan-card__price-period">{pricePeriod}</div>}
        </div>
      </div>

      <div className="plan-card__copy">
        <div className="plan-card__tagline">{tagline}</div>
        {description && (
          <div className="plan-card__description">{renderDescription(description)}</div>
        )}
        {note && <div className="plan-card__note">{note}</div>}
      </div>

      <div className="plan-card__cta">
        {tierGroup.contactSales ? (
          <a
            href="https://www.replay.io/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="plan-card__btn"
            data-analytics-event={`plan-contact-sales-link-${tierGroup.tier}`}
            onClick={() => trackEvent('contact-sales-clicked', { tier: tierGroup.tier, surface })}
          >
            Contact Sales
          </a>
        ) : isCurrentPlan ? (
          <button
            type="button"
            className="plan-card__btn"
            disabled
            data-analytics-event="plan-current-button"
          >
            {cancelAtPeriodEnd ? 'Update Scheduled' : 'Current Plan'}
          </button>
        ) : (
          <button
            type="button"
            className={`plan-card__btn${isHighlighted ? ' plan-card__btn--solid' : ''}`}
            disabled={isPending || (usePortalForChange && !workspaceId)}
            onClick={() => {
              trackEvent('plan-selected', {
                plan: tierGroup.name,
                tier: tierGroup.tier,
                interval,
                current_plan: currentPlanKey ?? 'free',
                change: planChangeDirection(currentPlanKey, tierGroup.tier),
                // Free → paid goes through Checkout; an existing subscriber's switch goes through the
                // portal, which is a different page and a different abandonment story.
                flow: usePortalForChange ? 'portal' : 'checkout',
                surface,
              })
              trackSegmentEvent('Changed Subscription', {
                // The billing catalog's name, NOT the display name — downstream destinations key
                // conversions off the plan Stripe reports, so a UI rename must not move it.
                change: planChangeDirection(currentPlanKey, tierGroup.tier),
                plan: tierGroup.name,
              })
              void (usePortalForChange ? handleOpenPortal : handleSubscribe)()
            }}
            data-analytics-event="plan-select-upgrade"
          >
            {isPending
              ? 'Redirecting…'
              : isExistingSubscriberChange
                ? 'Update Plan'
                : 'Get Started'}
          </button>
        )}
      </div>
    </div>
  )
}

const HIGHLIGHT_PHRASE = 'no credit card required'

/**
 * Render a plan description, applying a yellow highlighter effect to the
 * "no credit card required" phrase wherever it appears (the FREE plan).
 */
function renderDescription(description: string): ReactNode {
  const index = description.toLowerCase().indexOf(HIGHLIGHT_PHRASE)
  if (index === -1) {
    return description
  }
  const before = description.slice(0, index)
  const phrase = description.slice(index, index + HIGHLIGHT_PHRASE.length)
  const after = description.slice(index + HIGHLIGHT_PHRASE.length)
  return (
    <>
      {before}
      <mark className="plan-card__highlight">{phrase}</mark>
      {after}
    </>
  )
}

function formatPrice(plan: StripePlan): { priceNumber: string; pricePeriod: string } {
  // A contact-sales tier has no checkout, so its price is where the conversation starts rather than
  // what the customer will be billed — never present it as a flat monthly rate.
  if (plan.contactSales) {
    if (plan.monthlyPriceCents === null) {
      return { priceNumber: 'Custom', pricePeriod: 'usage-based or seat-based · negotiated together' }
    }
    const from = Math.round(plan.monthlyPriceCents / 100).toLocaleString('en-US')
    return { priceNumber: `$${from}+`, pricePeriod: 'per month · starting price' }
  }
  if (plan.monthlyPriceCents === null) {
    return { priceNumber: '—', pricePeriod: '' }
  }
  if (plan.monthlyPriceCents === 0) {
    return { priceNumber: '$0', pricePeriod: 'always free' }
  }

  const perMonth = Math.round(plan.monthlyPriceCents / 100)
  // Base the period label on the resolved plan's actual interval, not the
  // toggle. Tiers without a yearly variant fall back to their monthly plan in
  // the "yearly" slot, so they must not claim "billed annually".
  if (plan.interval === 'year') {
    return { priceNumber: `$${perMonth}`, pricePeriod: 'per month · billed annually' }
  }
  return { priceNumber: `$${perMonth}`, pricePeriod: 'per month' }
}
